import { 
  ClassCode, 
  ObstacleType, 
  Obstacle, 
  CourseConfiguration,
  DRF_2025_QUOTAS,
  DEFAULT_OBSTACLE_DISTRIBUTION,
  isSpecialObstacleAllowed
} from '../types/drf-rules';

export interface ObstaclePool {
  vertical: number;
  oxer: number;
  triple_bar: number;
  liverpool: number;
  open_water: number;
  combination: number;
  start: number;
  finish: number;
}

export function generateObstaclePool(classCode: ClassCode, isIndoor: boolean = false): ObstaclePool {
  const quota = DRF_2025_QUOTAS[classCode];
  const targetObstacles = Math.floor((quota.minObstacles + quota.maxObstacles) / 2);
  
  // Start with default distribution
  let pool: ObstaclePool = {
    vertical: Math.round(targetObstacles * DEFAULT_OBSTACLE_DISTRIBUTION.vertical / 100),
    oxer: Math.round(targetObstacles * DEFAULT_OBSTACLE_DISTRIBUTION.oxer / 100),
    triple_bar: Math.round(targetObstacles * DEFAULT_OBSTACLE_DISTRIBUTION.triple_bar / 100),
    liverpool: Math.round(targetObstacles * DEFAULT_OBSTACLE_DISTRIBUTION.liverpool / 100),
    open_water: Math.round(targetObstacles * DEFAULT_OBSTACLE_DISTRIBUTION.open_water / 100),
    combination: quota.minCombos,
    start: 0,
    finish: 0
  };

  // Apply class-specific restrictions
  if (!isSpecialObstacleAllowed(classCode, 'liverpool')) {
    pool.vertical += pool.liverpool;
    pool.liverpool = 0;
  }

  if (!isSpecialObstacleAllowed(classCode, 'open_water') || isIndoor) {
    pool.vertical += pool.open_water;
    pool.open_water = 0;
  }

  // Ensure we meet minimum oxer requirements
  const minOxers = quota.mustHave.oxersAtMax;
  if (pool.oxer < minOxers) {
    const deficit = minOxers - pool.oxer;
    pool.oxer = minOxers;
    pool.vertical = Math.max(0, pool.vertical - deficit);
  }

  // Adjust total to target
  const currentTotal = Object.values(pool).reduce((sum, count) => sum + count, 0);
  const difference = targetObstacles - currentTotal;
  
  if (difference > 0) {
    pool.vertical += difference;
  } else if (difference < 0) {
    pool.vertical = Math.max(0, pool.vertical + difference);
  }

  return pool;
}

export function validateCourse(config: CourseConfiguration, actualCombinations?: number): string[] {
  const errors: string[] = [];
  const quota = DRF_2025_QUOTAS[config.classCode];
  
  // Count only actual obstacles (exclude start/finish markers)
  const actualObstacles = config.obstacles.filter(obstacle => 
    obstacle.type !== 'start' && obstacle.type !== 'finish'
  );
  const obstacleCount = actualObstacles.length;

  // Check obstacle count
  if (obstacleCount < quota.minObstacles) {
    errors.push(`Utilstrækkelige spring: ${obstacleCount}/${quota.minObstacles} minimum`);
  }
  if (obstacleCount > quota.maxObstacles) {
    errors.push(`For mange spring: ${obstacleCount}/${quota.maxObstacles} maksimum`);
  }

  // Check combinations - use actualCombinations if provided, otherwise fall back to old logic
  let combinationCount: number;
  if (actualCombinations !== undefined) {
    combinationCount = actualCombinations;
  } else {
    // Fallback to old logic for backwards compatibility
    const combinations = actualObstacles.filter(o => o.isPartOfCombination);
    const combinationIds = new Set(combinations.map(o => o.combinationId));
    combinationCount = combinationIds.size;
  }

  if (combinationCount < quota.minCombos) {
    errors.push(`Utilstrækkelige kombinationer: ${combinationCount}/${quota.minCombos} minimum`);
  }
  if (combinationCount > quota.maxCombos) {
    errors.push(`For mange kombinationer: ${combinationCount}/${quota.maxCombos} maksimum`);
  }

  // Check combination rules (obstacle types and positions)
  const combinationErrors = validateCombinationRules(config);
  errors.push(...combinationErrors);

  // Check heights
  const heightOutOfRange = actualObstacles.filter(o => 
    o.height < quota.heightMin || o.height > quota.heightMax
  );
  if (heightOutOfRange.length > 0) {
    errors.push(`${heightOutOfRange.length} spring har højder udenfor ${quota.heightMin}-${quota.heightMax}cm området`);
  }

  // Check mandatory requirements for sværhedsgrad 1-4
  if (quota.sværhedsgrad >= 1 && quota.sværhedsgrad <= 4) {
    const maxHeightObstacles = actualObstacles.filter(o => o.height === quota.heightMax);
    const maxHeightOxers = maxHeightObstacles.filter(o => o.type === 'oxer');
    
    if (maxHeightObstacles.length < quota.mustHave.fencesAtMax) {
      errors.push(`Skal have mindst ${quota.mustHave.fencesAtMax} spring på maksimal højde (${quota.heightMax}cm)`);
    }
    if (maxHeightOxers.length < quota.mustHave.oxersAtMax) {
      errors.push(`Skal have mindst ${quota.mustHave.oxersAtMax} oxer på maksimal højde (${quota.heightMax}cm)`);
    }
  }

  // Check water jump width
  const waterJumps = actualObstacles.filter(o => o.type === 'open_water');
  if (waterJumps.length > 0) {
    if (!quota.waterMaxCm) {
      errors.push('Vandspring er ikke tilladt i denne klasse');
    } else {
      const invalidWater = waterJumps.filter(w => 
        !w.width || 
        w.width > quota.waterMaxCm! || 
        (quota.waterMinCm && w.width < quota.waterMinCm)
      );
      if (invalidWater.length > 0) {
        const range = quota.waterMinCm 
          ? `${quota.waterMinCm}-${quota.waterMaxCm}cm`
          : `maks ${quota.waterMaxCm}cm`;
        errors.push(`Vandspring bredde skal være ${range}`);
      }
    }
  }

  // Check for overlapping obstacles
  const overlaps = findOverlappingObstacles(config.obstacles);
  if (overlaps.length > 0) {
    errors.push(`${overlaps.length} spring overlapper hinanden`);
  }

  return errors;
}

// Helper function to calculate distance between two points
function calculateDistance(x1: number, y1: number, x2: number, y2: number): number {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}

// Validate combination rules for obstacle types and positions
function validateCombinationRules(config: CourseConfiguration): string[] {
  const errors: string[] = [];
  
  // Get course sequence
  const startObstacle = config.obstacles.find(o => o.type === 'start');
  const finishObstacle = config.obstacles.find(o => o.type === 'finish');
  
  const numberedObstacles = config.obstacles
    .filter(o => o.number && o.type !== 'start' && o.type !== 'finish')
    .sort((a, b) => (a.number || 0) - (b.number || 0));
  
  const sequence: any[] = [];
  if (startObstacle) sequence.push(startObstacle);
  sequence.push(...numberedObstacles);
  if (finishObstacle) sequence.push(finishObstacle);
  
  if (sequence.length < 2) return errors;
  
  // Check each segment for combination rules
  for (let i = 0; i < sequence.length - 1; i++) {
    const from = sequence[i];
    const to = sequence[i + 1];
    
    // Skip segments involving start/finish
    if (from.type === 'start' || from.type === 'finish' || 
        to.type === 'start' || to.type === 'finish') {
      continue;
    }
    
    // Calculate if this is a combination
    const segmentDistance = calculateDistance(from.x, from.y, to.x, to.y);
    const curveMultiplier = segmentDistance < 15 ? 1.2 : 1.1;
    const displayDistance = segmentDistance * curveMultiplier;
    
    const isCombination = config.animalType === 'horse' 
      ? (displayDistance >= 6 && displayDistance <= 12)
      : (displayDistance >= 5 && displayDistance <= 11);
    
    if (isCombination) {
      // Check combination rules
      
      // Rule 1: Open water is FORBIDDEN in combinations
      if (from.type === 'open_water' || to.type === 'open_water') {
        const waterObstacle = from.type === 'open_water' ? from : to;
        errors.push(`Åben vandgrav (spring ${waterObstacle.number}) er forbudt i kombination - skal altid stå alene`);
      }
      
      // Rule 2: Triple bar can ONLY be first element (A-led) in combination
      if (to.type === 'triple_bar') {
        // Triple bar as second element is forbidden
        errors.push(`Triple-barre (spring ${to.number}) må kun være første element (A-led) i kombination`);
      }
      
      // Rule 3: Liverpool is OK if class allows it (already checked in obstacle placement)
      // This is handled by the placement restrictions
    }
  }
  
  return errors;
}

function findOverlappingObstacles(obstacles: Obstacle[]): string[] {
  const overlaps: string[] = [];
  const gridSize = 0.5; // 50cm grid resolution

  for (let i = 0; i < obstacles.length; i++) {
    for (let j = i + 1; j < obstacles.length; j++) {
      const dist = Math.sqrt(
        Math.pow(obstacles[i].x - obstacles[j].x, 2) + 
        Math.pow(obstacles[i].y - obstacles[j].y, 2)
      );
      if (dist < gridSize) {
        overlaps.push(`Spring ${i + 1} og ${j + 1} overlapper`);
      }
    }
  }

  return overlaps;
}

export function createDefaultObstacle(type: ObstacleType, classCode: ClassCode, x: number, y: number, existingObstacles: Obstacle[] = []): Obstacle {
  const quota = DRF_2025_QUOTAS[classCode];
  const id = `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  let height = quota.heightMax;
  let width: number | undefined;
  let number: number | undefined;

  // Only assign numbers to actual jumps, not start/finish markers
  if (type !== 'start' && type !== 'finish') {
    const existingNumbers = existingObstacles
      .filter(o => o.type !== 'start' && o.type !== 'finish' && o.number)
      .map(o => o.number!)
      .sort((a, b) => a - b);
    
    // Find the next available number
    number = 1;
    for (const existingNumber of existingNumbers) {
      if (number === existingNumber) {
        number++;
      } else {
        break;
      }
    }
  }

  if (type === 'oxer') {
    width = Math.min(130, quota.heightMax * 0.9); // Standard oxer width
  } else if (type === 'open_water' && quota.waterMaxCm) {
    width = quota.waterMinCm || Math.floor(quota.waterMaxCm * 0.8);
    height = 0; // Water jumps are typically at ground level
  } else if (type === 'triple_bar') {
    width = Math.min(150, quota.heightMax); // Triple bar width
  } else if (type === 'start' || type === 'finish') {
    height = 0; // Start/finish markers are at ground level
    width = 350; // 3.5 meters wide like other obstacles
  }

  return {
    id,
    type,
    height,
    width,
    x,
    y,
    rotation: 0,
    number
  };
} 